import { useRefreshPartFilesOnError } from 'common/Hooks/useRefreshPartFilesOnError'
import { getMeasure } from 'common/Utils/StringUtilities'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StepViewer } from '@i-mas/step-viewer'
import { objToStl } from './functionality/objToStl'
import Loader from '../Loader'
import Modal from '../Modal/Modal'
import { useAnalyzeStep } from './useAnalyzeStep/useAnalyzeStep'

/** @type {Map<string, File>} */
const objToStlFileCache = new Map()

const StlViewer = ({
	// FILES:
	modelSrc, stepModelSrc, extension,

	// PART INFO:
	partName, partId, size, weight, stepAnalysis,

	// ELEMENT OPTIONS:
	windowSize, hideData,
}) => {

	// useConvertToStep --> will only be used if stepModelSrc is null and modelSrc is one of: SLDPRT|IPT|PRT|SAT|CATPART|X_T|X_B|3DM (no STEP|STP|STL|OBJ) and it should set stepModelSrc once retrieved -- TODO

	const { t } = useTranslation('naming')
	
	const { onLoadPartFileError } = useRefreshPartFilesOnError(partId)

	const [errorModalIsOpen, setErrorModalIsOpen] = useState(false)
	const [errorMessageTranslationKey, setErrorMessageTranslationKey] = useState(/** @type {StlViewerError} */(''))
	const ErrorModal = (
		<Modal
			isOpen={errorModalIsOpen}
			closeModal={() => { setErrorModalIsOpen(false) }}
			title={t(errorMessageTranslationKey, { ns: 'naming' })}
		/>
	)
	const showError = (/** @type {StlViewerError} */ errorMessageTranslationKey) => {
		setErrorMessageTranslationKey(errorMessageTranslationKey)
		setErrorModalIsOpen(true)
	}

	const {
		updatedStepAnalysis,
	} = useAnalyzeStep({
		partId,
		stepModelSrc,
		initialStepAnalysis: stepAnalysis,
		showError,
	})

	const [stlFile, setStlFile] = useState(/** @type {File} */(null))
	useEffect(() => {
		if (modelSrc == null) {
			onLoadPartFileError()
			return
		}

		const ext = (() => {
			// Solo el nombre del archivo: el pathname de una URL blob: es la URL de dentro
			// ("blob:https://dev.admin.protoandgo.es/<uuid>"), y los puntos del dominio se tomaban por la extensión.
			const extensionOfFileName = (value) => {
				const clean = value.split('?')[0]
				const fileName = clean.slice(clean.lastIndexOf('/') + 1)
				const dot = fileName.lastIndexOf('.')
				return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase()
			}

			if (!modelSrc.startsWith('blob:') && !modelSrc.startsWith('data:')) {
				try {
					const found = extensionOfFileName(new URL(modelSrc, window.location.href).pathname)
					if (found) return found
				} catch {
					const found = extensionOfFileName(modelSrc)
					if (found) return found
				}
			}
			// URLs blob:/data: y otras fuentes sin extensión: se usa la que indique quien monta el visor
			return extension ? extension.replace(/^\./, '').toLowerCase() : ''
		})()

		setStlFile(null)

		if (ext === 'stl') {
			fetch(modelSrc)
				.then(r => r.blob())
				.then(blob => setStlFile(new File([blob], 'model.stl', { type: 'model/stl' })))
				.catch(() => onLoadPartFileError())
		} else if (ext === 'obj') {
			if (objToStlFileCache.has(modelSrc)) {
				setStlFile(objToStlFileCache.get(modelSrc))
			} else {
				objToStl(modelSrc)
					.then(stlString => {
						const file = new File([stlString], 'model.stl', { type: 'model/stl' })
						objToStlFileCache.set(modelSrc, file)
						setStlFile(file)
					})
					.catch(() => onLoadPartFileError())
			}
		} else {
			showError('cannot_read_file_type')
		}
	}, [modelSrc])

	return (
		<>
			<div
				className="w-full text-center"
				style={{ background: 'linear-gradient(to bottom, #b2b2b2 0%, #ffffff 100%)' }}>

				{/* "Loading STL" Progress Bar */}
				{!stlFile && (
					<div
						style={{
							width: windowSize.width,
							height: windowSize.height,
							margin: '1em auto',
						}}
					>
						<Loader className="font-size-80 mx-auto" />
					</div>
				)}

				{/* STL Viewer with part info card overlaid */}
				{stlFile && (
					<div style={{ position: 'relative', display: 'inline-block' }}>
						<StepViewer
							color={'#f78e1e'}
							showHolesCard
							showTopBand={!hideData}
							size={hideData || !size?.x || !size?.y || !size?.z ? undefined : { x: Number(size.x), y: Number(size.y), z: Number(size.z) }}
							partName={hideData ? undefined : partName}
							weight={hideData || !weight ? undefined : getMeasure(weight.value ? weight.value : weight, weight.measure ?? 'g')}
							stlFile={stlFile}
							stepAnalysis={updatedStepAnalysis}
							windowSize={windowSize}
							onError={onLoadPartFileError}
						/>
					</div>
				)}

			</div>
			{ErrorModal}
		</>
	)
}

StlViewer.propTypes = {
	// FILES:
	modelSrc: PropTypes.string,
	stepModelSrc: PropTypes.string,
	extension: PropTypes.string,

	// PART INFO:
	partName: PropTypes.string,
	partId: PropTypes.number,
	size: PropTypes.object,
	weight: PropTypes.object,
	stepAnalysis: PropTypes.any,

	// ELEMENT OPTIONS:
	windowSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
	hideData: PropTypes.bool,
}

/**
 * @typedef {object} StlViewerProps
 * @property {string} modelSrc
 * @property {string} [stepModelSrc]
 * @property {string} partName
 * @property {number} partId
 * @property {object} size
 * @property {object} weight
 * @property {any} stepAnalysis
 * @property {{ width: number, height: number }} windowSize
 * @property {boolean} [hideData]
 */

export default StlViewer
